/**
 * Jarvis (pages/Jarvis.tsx, the Agency overview box, Settings → Jarvis): types, starter
 * questions and labels. Only erasable types, so node --test loads this file directly.
 */
export type JarvisAction = { type: "task"; id: number; title: string; assigned_to?: number; client_id?: number | null; due_date?: string | null; to: string };
export type JarvisMessage = {
  id: number | string; role: "user" | "assistant"; content: string; created_at?: string;
  steps?: string[]; actions?: JarvisAction[]; provider?: string | null; model?: string | null; failed?: boolean;
  attachments?: JarvisAttachment[];
};
export type JarvisEvent =
  | { type: "ping" }
  | { type: "session"; id: number; title?: string; attachments?: JarvisAttachment[] }
  | { type: "step"; label: string }
  | { type: "action"; action: JarvisAction }
  | { type: "provider"; provider: string; model: string }
  | { type: "done"; message: JarvisMessage }
  | { type: "error"; error: string };
export type JarvisChoice = { id: "claude" | "openai"; label: string; model: string; connected: boolean; via: string | null };
export type JarvisSettings = { provider: JarvisChoice["id"]; choices: JarvisChoice[]; models?: JarvisModel[]; default_model?: string | null };

export const JARVIS_STARTERS = [
  "Which clients need attention this week, and why?",
  "How did ad spend and leads do across the agency in the last 30 days?",
  "What is on my plate today?",
  "What is waiting for approval?",
];

/** The AI that wrote a reply, in words: "Claude" or "OpenAI". */
export function providerLabel(provider?: string | null, model?: string | null) {
  if (provider === "openai" || /^(gpt|o\d)/i.test(model || "") || /^openai\//.test(model || "")) return "OpenAI";
  if (provider === "anthropic" || /claude/i.test(model || "")) return "Claude";
  return provider === "openrouter" ? "OpenRouter" : provider || "";
}

/** The Jarvis page URL that asks a question as it opens. */
export const askJarvis = (question: string) => `/jarvis?ask=${encodeURIComponent(question.trim())}&go=1`;

export type JarvisSession = { id: number; title: string | null; updated_at: string; running?: boolean };
export type JarvisSessionGroup = { label: string; items: JarvisSession[] };

const DAY_MS = 86_400_000;
const startOfDay = (t: number) => { const d = new Date(t); d.setHours(0, 0, 0, 0); return d.getTime(); };

/** Past conversations for the history rail: matching the search, newest first, under Today,
 * Yesterday, Previous 7 days and Older. Empty groups are left out. */
export function groupJarvisSessions(sessions: JarvisSession[], search = "", now = Date.now()): JarvisSessionGroup[] {
  const q = search.trim().toLowerCase();
  const today = startOfDay(now);
  const groups: JarvisSessionGroup[] = [{ label: "Today", items: [] }, { label: "Yesterday", items: [] }, { label: "Previous 7 days", items: [] }, { label: "Older", items: [] }];
  for (const s of sessions) {
    if (q && !jarvisTitle(s).toLowerCase().includes(q)) continue;
    const t = new Date(s.updated_at).getTime() || 0;
    groups[t >= today ? 0 : t >= today - DAY_MS ? 1 : t >= today - 7 * DAY_MS ? 2 : 3].items.push(s);
  }
  for (const g of groups) g.items.sort((a, b) => (new Date(b.updated_at).getTime() || 0) - (new Date(a.updated_at).getTime() || 0));
  return groups.filter((g) => g.items.length);
}

/** A conversation's name in the rail and the chat header. */
export const jarvisTitle = (s: Pick<JarvisSession, "id" | "title">) => (s.title || "").trim() || "Untitled chat";

/** When a conversation was last active: a time today, a weekday this week, else a date. */
export function jarvisWhen(updatedAt: string, now = Date.now()) {
  const t = new Date(updatedAt).getTime();
  if (!t) return "";
  const today = startOfDay(now);
  if (t >= today) return new Date(t).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  if (t >= today - 6 * DAY_MS) return new Date(t).toLocaleDateString("en-US", { weekday: "short" });
  return new Date(t).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export type JarvisAttachment = { id: number; name: string; mime: string; kind: "image" | "pdf" | "text"; size: number };
export type JarvisModel = { id: string; label: string; group?: string; hint?: string; connected: boolean; usual?: boolean };

/** What the attach button offers; the server checks each file again (utils/jarvisFiles.js). */
export const JARVIS_ACCEPT = ".png,.jpg,.jpeg,.webp,.gif,.pdf,.txt,.csv,.tsv,.md,.json,.html,.htm,.xml,.log,.yaml,.yml,.xlsx,.xls";
export const JARVIS_MAX_FILES = 8;
export const JARVIS_MAX_BYTES = 4 * 1024 * 1024;

/** "820 KB", "1.4 MB". */
export function fileSizeLabel(bytes: number) {
  if (!Number.isFinite(bytes) || bytes < 0) return "";
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** The picker's rows under their AI ("Claude", "OpenAI"), in the server's order. */
export function groupModels(models: JarvisModel[]) {
  const groups: { label: string; models: JarvisModel[] }[] = [];
  for (const m of models) {
    const label = m.group || "Models";
    const g = groups.find((x) => x.label === label);
    if (g) g.models.push(m); else groups.push({ label, models: [m] });
  }
  return groups;
}

/** The model the message box starts on: the one picked last time if it is still offered and connected, else the agency's default. */
export function startingModel(models: JarvisModel[], saved: string | null, fallback: string) {
  const ok = (id: string | null) => models.find((m) => m.id === id && m.connected)?.id;
  // The first picker saved "claude", "claude-fast" and "openai".
  const usualOf = (prefix: string) => models.find((m) => m.connected && m.usual && m.id.startsWith(prefix))?.id || models.find((m) => m.connected && m.id.startsWith(prefix))?.id || null;
  const older = saved === "claude-fast" ? ok("anthropic:claude-sonnet-5-5") : saved === "claude" ? usualOf("anthropic:") : saved === "openai" ? usualOf("openai:") : null;
  return ok(saved) || older || ok(fallback) || models.find((m) => m.connected)?.id || fallback;
}

/** A model id in words: "claude-opus-5-5" → "Claude Opus 5.5", "gpt-6-astra" → "GPT-6 Astra". Empty when unknown. */
export function modelLabel(model?: string | null) {
  const id = String(model || "").replace(/^(anthropic|openai)\//, "");
  const claude = /^claude-([a-z]+)-(\d+)(?:[-.](\d+))?$/.exec(id);
  if (claude) return `Claude ${claude[1][0].toUpperCase()}${claude[1].slice(1)} ${claude[2]}${claude[3] ? `.${claude[3]}` : ""}`;
  if (/^gpt-/.test(id)) return id.replace(/^gpt-/, "GPT-").split("-").map((w, i) => (i === 0 ? w : w[0].toUpperCase() + w.slice(1))).join(" ").replace(/^GPT /, "GPT-");
  return "";
}
